import "@xyflow/react/dist/style.css";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SelectionModal from "~/components/Summary/importSelectionModal";
import Button from "~/components/ui/button";
import CreditLackModal from "~/components/ui/creditLackModal";
import { DOCUMENT_TYPES } from "~/config/documents";
import { TOAST_MESSAGES } from "~/constants/messages";
import { GenerateApi } from "~/functions/api/generate";
import { useAppGenerationNotifications } from "~/hooks/useAppGenerationNotifications";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import ChartIcon from "~/images/chart.svg?react";
import ClockLoaderIcon from "~/images/clock_loader.svg?react";
import DocumentIcon from "~/images/document.svg?react";
import HistoryIcon from "~/images/history.svg?react";
import KintoneIcon from "~/images/kintone-logo.svg?react";
import TableIcon from "~/images/table.svg?react";
import type { CommonGenerationBody } from "~/interface/generate";
import { setCurrentProjectOutputsDocumentType, setOrganization } from "~/slices/auth";
import type { RootState } from "~/store";
import { generateGroupName, joinGroup, leaveGroup } from "~/utils/webPubSubUtils";
import { useWebPubSub } from "../webPubSubContext";
import styles from "./index.module.scss";

interface Props {
  statusUpdates?: { id: string; status: boolean }[];
}

type StepCardProps = {
  icon: React.ReactNode;
  title: React.ReactNode;
  action: string;
  actionIsDisabled?: boolean;
  name: string;
  generate?: string | (() => Promise<void>);
  withBorder?: boolean;
  isGeneratable?: boolean;
  onClick?: () => void;
  isGenerating?: boolean;
  isGenerated?: boolean;
  isDisabled?: boolean;
  isOpenButton?: boolean;
  documentType?: keyof typeof DOCUMENT_TYPES;
  projectId?: string;
  currentProject?: any;
  creditUsage?: number;
  canGenerate?: boolean;
  helpText?: string;
};

const StepCard: React.FC<StepCardProps> = ({
  icon,
  title,
  action,
  actionIsDisabled = false,
  name,
  generate,
  withBorder = false,
  isGeneratable = true,
  onClick,
  isOpenButton,
  projectId,
  currentProject,
  creditUsage,
  canGenerate = true,
  helpText,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isGeneratingState, setIsGeneratingState] = useState(false);
  const { client } = useWebPubSub();
  const mode = "generate";

  // システム概要書の生成状態を確認
  const systemProposalOutput = currentProject?.outputs?.["system-proposal"];
  const targetOutput = currentProject?.outputs?.[name];
  const isSystemProposalGenerated = Boolean(systemProposalOutput?.id);
  // isGeneratingがtrueの場合のみ生成中と判定
  const isDocumentGenerating = Boolean(targetOutput?.isGenerating === true);
  const isEstimationCard = name === "estimation";
  const isCostEffectivenessCard = name === "cost-effectiveness";
  const isDeployCard = name === "deploy";
  const canOpenEstimation = Boolean(currentProject?.outputs?.["estimation"]?.id);
  const canOpenCostEffectiveness = canOpenEstimation;
  const shouldShowOpenButton = (() => {
    if (!isOpenButton) {
      return false;
    }
    if (isEstimationCard) {
      return canOpenEstimation;
    }
    if (isCostEffectivenessCard) {
      return canOpenCostEffectiveness;
    }
    return Boolean(isSystemProposalGenerated);
  })();

  // 次のドキュメントの生成状態をチェックする関数を修正
  const isNextDocumentGenerating = () => {
    // 現在のドキュメントが生成中かどうかをチェック
    if (targetOutput?.isGenerating === true) {
      return true;
    }

    // 依存関係のあるドキュメントが生成中かどうかをチェック
    const dependencies = {
      "system-proposal": ["summary"],
      "functional-requirements": ["system-proposal"],
      "non-functional-requirements": ["system-proposal"],
      workflow: ["system-proposal"],
      "plugin-proposal": ["functional-requirements"],
      "app-design": ["functional-requirements"],
      "field-design": ["app-design"],
      "system-architecture": ["system-proposal", "app-design"],
      "entity-relationship": ["field-design"],
      "customize-specification": ["field-design", "app-design"],
    };

    const docDependencies = dependencies[name as keyof typeof dependencies] || [];

    // 依存ドキュメントが生成中で、かつ現在のドキュメントが未生成の場合のみtrueを返す
    return docDependencies.some(
      (dep) => currentProject?.outputs?.[dep]?.isGenerating === true && !targetOutput?.id, // 現在のドキュメントが未生成の場合
    );
  };

  // ボタンの無効化条件を更新
  const isButtonDisabled = isDeployCard
    ? !canGenerate || !currentProject?.outputs?.["field-design"]?.id
    : isGeneratingState || isDocumentGenerating || isNextDocumentGenerating() || !canGenerate;

  useEffect(() => {
    const joinGroupIfGenerating = async () => {
      // isGeneratingがtrueの場合のみグループに参加
      if (
        name &&
        currentProject?.outputs &&
        name in currentProject.outputs &&
        targetOutput?.isGenerating === true &&
        client &&
        currentProject?.id
      ) {
        try {
          const groupName = generateGroupName(currentProject.id, name, mode);
          await joinGroup(client, groupName);
        } catch (error) {
          console.error(`Failed to join group for ${name}:`, error);
        }
      }
    };

    joinGroupIfGenerating();
  }, [client, name, currentProject?.id, currentProject?.outputs, targetOutput?.isGenerating]);

  const handleClick = async () => {
    if (isDeployCard) {
      // フィールド設計書が未生成の場合は遷移させない
      const fieldDesignGenerated = Boolean(currentProject?.outputs?.["field-design"]?.id);
      if (!fieldDesignGenerated) {
        toast.error(TOAST_MESSAGES.GENERATION.FIELD_DESIGN_NOT_GENERATED);
        return;
      }
      if (!currentProject?.organization_id || !projectId) return;
      navigate(`/${currentProject.organization_id}/project/${projectId}/app-generation`);
      return;
    }
    if (actionIsDisabled) {
      toast.error(TOAST_MESSAGES.GENERATION.PLAN_NOT_AVAILABLE);
      return;
    }

    if (isGeneratingState) return;

    if (isEstimationCard) {
      const estimationDocumentId = currentProject?.outputs?.["estimation"]?.id;
      if (!estimationDocumentId) {
        if (currentProject?.outputs?.["estimation"]?.isGenerating) {
          toast.info(TOAST_MESSAGES.GENERATION.ESTIMATION_GENERATING);
        } else {
          toast.error(TOAST_MESSAGES.GENERATION.ESTIMATION_NOT_GENERATED);
        }
        return;
      }
      const estimationSegments = location.pathname.split("/").filter(Boolean);
      estimationSegments.pop();
      estimationSegments.push("estimation");
      estimationSegments.push(estimationDocumentId);
      navigate(`/${estimationSegments.join("/")}`);
      return;
    }

    if (isCostEffectivenessCard) {
      if (!canOpenCostEffectiveness) {
        toast.error(TOAST_MESSAGES.GENERATION.ESTIMATION_NOT_GENERATED);
        return;
      }
      const costEffectivenessSegments = location.pathname.split("/").filter(Boolean);
      costEffectivenessSegments.pop();
      costEffectivenessSegments.push("cost-effectiveness");
      navigate(`/${costEffectivenessSegments.join("/")}`);
      return;
    }

    const segments = location.pathname.split("/").filter(Boolean);
    segments.pop();
    segments.push(name);

    // 生成中または未生成の場合の遷移を防ぐ（summaryは除く）
    if (name !== "summary" && !targetOutput?.id && typeof generate !== "function") {
      if (targetOutput?.isGenerating) {
        toast.info(TOAST_MESSAGES.GENERATION.DOCUMENT_GENERATING(title as string));
      } else {
        toast.error(TOAST_MESSAGES.GENERATION.DOCUMENT_NOT_GENERATED(title as string));
      }
      return;
    }

    if (name !== "summary" && typeof generate !== "function" && targetOutput?.id) {
      segments.push(targetOutput.id);
    }

    if (isOpenButton) {
      if (shouldShowOpenButton) {
        const documentId = targetOutput?.id;
        if (documentId) {
          navigate(
            `/${currentProject.organization_id}/project/${projectId}/${name.toLowerCase()}/${documentId}`,
          );
        } else {
          navigate(`/${currentProject.organization_id}/project/${projectId}/${name.toLowerCase()}`);
        }
      }
      return;
    }

    if (typeof generate === "function") {
      setIsGeneratingState(true);
      try {
        await generate();
      } catch (error) {
        console.error(`Generation failed for ${name}:`, error);
      } finally {
        setIsGeneratingState(false);
      }
    }
    navigate(`/${segments.join("/")}`);
  };

  // 生成状態の判定
  const getGenerationStatus = () => {
    // isGeneratingがtrueの場合のみ生成中と表示
    if (targetOutput?.isGenerating === true) {
      return "生成中";
    } else if (targetOutput?.id) {
      return "生成済";
    } else {
      return "未生成";
    }
  };

  return (
    <div
      className={`${styles["card"]} ${withBorder ? styles["withBorder"] : ""}`}
      onClick={
        (!isEstimationCard && !isCostEffectivenessCard) || shouldShowOpenButton
          ? handleClick
          : undefined
      }
      style={{
        cursor:
          (!isEstimationCard && !isCostEffectivenessCard) || shouldShowOpenButton
            ? "pointer"
            : "default",
      }}
    >
      <div className={styles["cardHeader"]}>
        <span>
          {icon}
          {title}
        </span>
        {helpText && (
          <Tooltip title={helpText} arrow placement="top">
            <InfoOutlinedIcon
              className={styles["helpIcon"] ?? ""}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              data-testid={`help-icon-${name}`}
            />
          </Tooltip>
        )}
        <div
          className={`${styles["buttonContainer"]} ${isButtonDisabled ? styles["generating"] : ""}`}
          onClick={async (e) => {
            e.stopPropagation();
            if (actionIsDisabled) {
              toast.error(TOAST_MESSAGES.GENERATION.PLAN_NOT_AVAILABLE);
              return;
            }
            if (onClick && !isButtonDisabled) {
              try {
                setIsGeneratingState(true);
                await onClick();
              } catch (_err) {
                // noop; individual handlers toast errors
              } finally {
                setIsGeneratingState(false);
              }
            }
          }}
        >
          {creditUsage !== undefined && !isButtonDisabled && (
            <div className={styles["creditTooltip"]}>
              {creditUsage === 0 ? "クレジット消費なし" : `${creditUsage} クレジット消費`}
            </div>
          )}
          {isGeneratable && (
            <Button
              label={action}
              styleType="white-small"
              disabled={isButtonDisabled}
              dataTestId={`generate-${name}`}
            />
          )}
        </div>
      </div>
      {!isCostEffectivenessCard && !isDeployCard && (
        <div className={styles["time"]} data-testid={`status-${name}`}>
          {targetOutput?.isGenerating === true ? (
            <ClockLoaderIcon width={14} height={14} />
          ) : (
            <HistoryIcon width={14} height={14} />
          )}
          {getGenerationStatus()}
        </div>
      )}
    </div>
  );
};

const Dashboard: React.FC<Props> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreditLackModalOpen, setIsCreditLackModalOpen] = useState(false);
  const [lackCredit, setLackCredit] = useState(0);
  const { client } = useWebPubSub();
  const dispatch = useDispatch();
  const { currentUser, currentProject, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const mode = "generate";

  // プロジェクトメンバーの取得を保証（直接URL遷移時にも対応）
  useProjectMembers();
  // 権限チェック用のフックを使用
  const { canGenerateDocuments } = useProjectPermission();

  // アプリ生成通知用のフック
  useAppGenerationNotifications(client);

  const navigate = useNavigate();
  const location = useLocation();
  const isStarterPlan = organization?.plan && organization.plan.type === "starter";
  const generateApi = new GenerateApi();

  if (!currentProject || client === null) {
    return <div>プロジェクト情報を取得できませんでした</div>;
  }

  // モーダルを閉じる
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  const handleOpenModal = () => {
    // Check if organization has enough credits for summary generation
    if (organization) {
      const creditCost = DOCUMENT_TYPES.summary.creditUsage.generate;
      if (organization.remainingCredit < creditCost) {
        setLackCredit(creditCost - organization.remainingCredit);
        setIsCreditLackModalOpen(true);
        return;
      }
    }
    setIsModalOpen(true);
  };

  const checkCreditAndGenerate = (creditCost: number, generateFunction: () => Promise<void>) => {
    if (organization && organization.remainingCredit < creditCost) {
      setLackCredit(creditCost - organization.remainingCredit);
      setIsCreditLackModalOpen(true);
      return;
    }
    generateFunction();
  };

  const handleGenerateSystemProposal = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["system-proposal"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const isRegenerate = !!currentProject.outputs?.["system-proposal"]?.id;
        const generateMode = "standard";
        const requestBody: CommonGenerationBody = {
          options: {
            system_proposal: {
              regenerate: isRegenerate,
              mode: generateMode,
            },
          },
        };

        // 楽観的に生成中を反映（即時無効化を維持）
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "system-proposal",
            documentId: currentProject.outputs?.["system-proposal"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "system-proposal", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateSystemProposal(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          // Update organization's remaining credit locally
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.SYSTEM_OVERVIEW_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          // 失敗時はロールバック
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "system-proposal",
              documentId: currentProject.outputs?.["system-proposal"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateEstimation = async () => {
    try {
      const creditCost = DOCUMENT_TYPES.estimation.creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "estimation",
            documentId: currentProject.outputs?.["estimation"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "estimation", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateEstimation(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.ESTIMATION_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "estimation",
              documentId: currentProject.outputs?.["estimation"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateFunctionalRequirements = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["functional-requirements"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "functional-requirements",
            documentId: currentProject.outputs?.["functional-requirements"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "functional-requirements", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateFunctionalRequirements(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.FUNC_REQUIREMENT_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "functional-requirements",
              documentId: currentProject.outputs?.["functional-requirements"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateNonFunctionalRequirements = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["non-functional-requirements"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "non-functional-requirements",
            documentId: currentProject.outputs?.["non-functional-requirements"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "non-functional-requirements", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateNonFunctionalRequirements(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.NON_FUNC_REQUIREMENT_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "non-functional-requirements",
              documentId: currentProject.outputs?.["non-functional-requirements"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateWorkflow = async () => {
    try {
      const creditCost = DOCUMENT_TYPES.workflow.creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "workflow",
            documentId: currentProject.outputs?.["workflow"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "workflow", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateWorkflow(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.WORKFLOW_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "workflow",
              documentId: currentProject.outputs?.["workflow"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGeneratePluginProposal = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["plugin-proposal"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "plugin-proposal",
            documentId: currentProject.outputs?.["plugin-proposal"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "plugin-proposal", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generatePluginProposal(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.PLUGIN_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "plugin-proposal",
              documentId: currentProject.outputs?.["plugin-proposal"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateAppDesign = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["app-design"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "app-design",
            documentId: currentProject.outputs?.["app-design"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "app-design", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateAppDesign(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.APP_DESIGN_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "app-design",
              documentId: currentProject.outputs?.["app-design"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateFieldDesign = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["field-design"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "field-design",
            documentId: currentProject.outputs?.["field-design"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "field-design", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateFieldDesign(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.FIELD_DESIGN_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "field-design",
              documentId: currentProject.outputs?.["field-design"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateEntityRelationship = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["entity-relationship"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "entity-relationship",
            documentId: currentProject.outputs?.["entity-relationship"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "entity-relationship", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateEntityRelationship(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.ER_DIAGRAM_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "entity-relationship",
              documentId: currentProject.outputs?.["entity-relationship"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateSystemArchitecture = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["system-architecture"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "system-architecture",
            documentId: currentProject.outputs?.["system-architecture"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "system-architecture", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateSystemArchitecture(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.SYSTEM_ARCHITECTURE_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "system-architecture",
              documentId: currentProject.outputs?.["system-architecture"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };
  const handleGenerateCustomizeSpecification = async () => {
    try {
      const creditCost = DOCUMENT_TYPES["customize-specification"].creditUsage.generate;
      checkCreditAndGenerate(creditCost, async () => {
        const requestBody: CommonGenerationBody = {};
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType: "customize-specification",
            documentId: currentProject.outputs?.["customize-specification"]?.id || "",
            isGenerating: true,
          }),
        );
        const groupName = generateGroupName(currentProject.id, "customize-specification", mode);
        if (client !== null) {
          await joinGroup(client, groupName);
        }
        const response = await generateApi.generateCustomizeSpecification(
          currentUser.organization_id,
          currentProject.id,
          requestBody,
        );
        if (response !== null) {
          if (organization) {
            dispatch(
              setOrganization({
                ...organization,
                remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
              }),
            );
          }
          toast.success(TOAST_MESSAGES.GENERATION.CUSTOMIZE_SPEC_REQUEST_SUCCESS);
        } else {
          if (client !== null) leaveGroup(client, groupName);
          dispatch(
            setCurrentProjectOutputsDocumentType({
              documentType: "customize-specification",
              documentId: currentProject.outputs?.["customize-specification"]?.id || "",
              isGenerating: false,
            }),
          );
        }
      });
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
    }
  };

  return (
    <div data-testid="dashboard-root">
      <section className={styles["stepSection"]}>
        <h2 className={styles["step"]}>STEP1</h2>
        <StepCard
          icon={<DocumentIcon width={20} height={20} />}
          title="議事録"
          action="生成"
          name="summary"
          {...(currentProject.outputs?.["summary"]?.id && {
            generate: currentProject.outputs["summary"].id,
          })}
          onClick={handleOpenModal}
          creditUsage={DOCUMENT_TYPES.summary.creditUsage.generate}
          currentProject={currentProject}
          canGenerate={canGenerateDocuments()}
        />
      </section>
      <p className={styles["note"]}>議事録をもとにシステム概要書を生成</p>
      <section className={styles["stepSection2"]}>
        <div className={styles["stepSection"]}>
          <h2>STEP2</h2>
          <StepCard
            icon={<DocumentIcon width={20} height={20} />}
            title="システム概要書"
            action="生成"
            name="system-proposal"
            {...(currentProject.outputs?.["system-proposal"]?.id && {
              generate: currentProject.outputs["system-proposal"].id,
            })}
            isGeneratable={
              !!currentProject.outputs?.["summary"] &&
              (!!currentProject.outputs?.["system-proposal"]?.id ||
                !currentProject.outputs?.["summary"]?.isGenerating)
            }
            onClick={handleGenerateSystemProposal}
            creditUsage={DOCUMENT_TYPES["system-proposal"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
        </div>

        <div className={styles["cardRow2"]}>
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="概算工数見積もり"
            action="生成"
            name="estimation"
            withBorder
            isGeneratable={Boolean(currentProject.outputs?.["system-proposal"]?.id)}
            onClick={handleGenerateEstimation}
            creditUsage={DOCUMENT_TYPES.estimation.creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
            isOpenButton
            projectId={currentProject.id}
          />
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="費用対効果"
            action="開く"
            name="cost-effectiveness"
            withBorder
            isGeneratable={Boolean(currentProject.outputs?.["estimation"]?.id)}
            onClick={() => {
              if (!currentProject.outputs?.["estimation"]?.id) {
                toast.error(TOAST_MESSAGES.GENERATION.ESTIMATION_REQUIRED);
                return;
              }
              const segments = location.pathname.split("/").filter(Boolean);
              segments.pop();
              segments.push("cost-effectiveness");
              navigate(`/${segments.join("/")}`);
            }}
            currentProject={currentProject}
            isOpenButton
            projectId={currentProject.id}
          />
        </div>
      </section>
      <p className={styles["note"]}>システム概要書をもとに要件定義書を生成</p>
      <section className={styles["stepSection"]}>
        <h2>STEP3</h2>
        <h3 className={styles["stepTitle"]}>要件定義書</h3>
        <div className={styles["cardRow"]}>
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="機能要件書"
            action="生成"
            name="functional-requirements"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["functional-requirements"]?.id && {
              generate: currentProject.outputs["functional-requirements"].id,
            })}
            withBorder
            isGeneratable={!!currentProject.outputs?.["system-proposal"]?.id}
            onClick={handleGenerateFunctionalRequirements}
            creditUsage={DOCUMENT_TYPES["functional-requirements"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="非機能要件書"
            action="生成"
            name="non-functional-requirements"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["non-functional-requirements"]?.id && {
              generate: currentProject.outputs["non-functional-requirements"].id,
            })}
            withBorder
            isGeneratable={!!currentProject.outputs?.["system-proposal"]?.id}
            onClick={handleGenerateNonFunctionalRequirements}
            creditUsage={DOCUMENT_TYPES["non-functional-requirements"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="業務フロー図"
            action="生成"
            name="workflow"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["workflow"]?.id && {
              generate: currentProject.outputs["workflow"].id,
            })}
            withBorder
            isGeneratable={!!currentProject.outputs?.["system-proposal"]?.id}
            onClick={handleGenerateWorkflow}
            creditUsage={DOCUMENT_TYPES.workflow.creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
        </div>
        <p className={styles["note"]}>機能要件書をもとにプラグイン提案書を生成</p>
        <StepCard
          icon={<TableIcon width={20} height={20} />}
          title="プラグイン提案書"
          action="生成"
          name="plugin-proposal"
          actionIsDisabled={isStarterPlan}
          {...(currentProject.outputs?.["plugin-proposal"]?.id && {
            generate: currentProject.outputs["plugin-proposal"].id,
          })}
          withBorder
          isGeneratable={!!currentProject.outputs?.["functional-requirements"]?.id}
          onClick={handleGeneratePluginProposal}
          creditUsage={DOCUMENT_TYPES["plugin-proposal"].creditUsage.generate}
          currentProject={currentProject}
          canGenerate={canGenerateDocuments()}
          helpText="※生成には機能要件書にプラグインの使用に関する記載が必要です。"
        />
      </section>
      <p className={styles["note"]}>要件定義の内容をもとに基本設計書を生成</p>
      <section className={styles["stepSectionWide"]}>
        <h2>STEP4</h2>
        <h3 className={styles["stepTitle"]}>基本設計書</h3>
        <StepCard
          icon={<DocumentIcon width={20} height={20} />}
          title="アプリ設計書"
          action="生成"
          name="app-design"
          actionIsDisabled={isStarterPlan}
          {...(currentProject.outputs?.["app-design"]?.id && {
            generate: currentProject.outputs["app-design"].id,
          })}
          withBorder
          isGeneratable={!!currentProject.outputs?.["functional-requirements"]?.id}
          onClick={handleGenerateAppDesign}
          creditUsage={DOCUMENT_TYPES["app-design"].creditUsage.generate}
          currentProject={currentProject}
          canGenerate={canGenerateDocuments()}
        />
        <p className={styles["note"]}>アプリ設計書をもとにシステム構成図,フィールド設計書を生成</p>
        <div className={styles["cardRow"]}>
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="フィールド設計書"
            action="生成"
            name="field-design"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["field-design"]?.id && {
              generate: currentProject.outputs["field-design"].id,
            })}
            withBorder
            isGeneratable={!!currentProject.outputs?.["app-design"]?.id}
            onClick={handleGenerateFieldDesign}
            creditUsage={DOCUMENT_TYPES["field-design"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="システム構成図"
            action="生成"
            name="system-architecture"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["system-architecture"]?.id && {
              generate: currentProject.outputs["system-architecture"].id,
            })}
            withBorder
            isGeneratable={
              !!(
                currentProject.outputs?.["system-proposal"]?.id &&
                currentProject.outputs?.["app-design"]?.id
              )
            }
            onClick={handleGenerateSystemArchitecture}
            creditUsage={DOCUMENT_TYPES["system-architecture"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
        </div>
        <p className={styles["note"]}>フィールド設計書をもとにER図,カスタマイズ仕様書を生成</p>
        <div className={styles["cardRow"]}>
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="ER図"
            action="生成"
            name="entity-relationship"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["entity-relationship"]?.id && {
              generate: currentProject.outputs["entity-relationship"].id,
            })}
            withBorder
            isGeneratable={!!currentProject.outputs?.["field-design"]?.id}
            onClick={handleGenerateEntityRelationship}
            creditUsage={DOCUMENT_TYPES["entity-relationship"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
          />
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="カスタマイズ仕様書"
            action="生成"
            name="customize-specification"
            actionIsDisabled={isStarterPlan}
            {...(currentProject.outputs?.["customize-specification"]?.id && {
              generate: currentProject.outputs["customize-specification"].id,
            })}
            withBorder
            isGeneratable={
              !!(
                currentProject.outputs?.["field-design"]?.id &&
                currentProject.outputs?.["app-design"]?.id
              )
            }
            onClick={handleGenerateCustomizeSpecification}
            creditUsage={DOCUMENT_TYPES["customize-specification"].creditUsage.generate}
            currentProject={currentProject}
            canGenerate={canGenerateDocuments()}
            helpText="※生成にはアプリ設計書にカスタマイズの使用に関する記載が必要です。"
          />
        </div>
      </section>
      <p className={styles["note"]}>基本設計書の内容をもとに、kintoneアプリを生成</p>
      <section className={styles["stepSection"]}>
        <h2>STEP5</h2>
        <h3 className={styles["stepTitle"]}>生成</h3>
        <StepCard
          icon={<KintoneIcon width={20} height={20} />}
          title={"アプリ生成"}
          action="開く"
          name="deploy"
          withBorder
          isGeneratable={true}
          onClick={() => {
            const fieldDesignGenerated = Boolean(currentProject.outputs?.["field-design"]?.id);
            if (!fieldDesignGenerated) {
              toast.error(TOAST_MESSAGES.GENERATION.FIELD_DESIGN_NOT_GENERATED);
              return;
            }
            // app-generationページに遷移
            navigate(
              `/${currentProject.organization_id}/project/${currentProject.id}/app-generation`,
            );
          }}
          currentProject={currentProject}
          projectId={currentProject.id}
          canGenerate={canGenerateDocuments()}
        />
      </section>
      {isModalOpen && <SelectionModal onClose={handleCloseModal} />}
      <CreditLackModal
        isOpen={isCreditLackModalOpen}
        onClose={() => setIsCreditLackModalOpen(false)}
        lackCredit={lackCredit}
      />
    </div>
  );
};

export default Dashboard;
